import { Controller } from "@hotwired/stimulus"

// The share modal's one "Add people or emails" field. Typing filters the
// teammates and connections the owner can share with (rendered as JSON, so
// no request per keystroke); picking one posts to TaskListSharesController
// #create with their id. Anything else typed is an email address and posts
// to #invite, which shares with an existing account or sends an invite.
export default class extends Controller {
    static targets = ["form", "input", "userId", "results", "submit"]
    static values = { people: Array, inviteUrl: String, shareUrl: String }

    static MAX_RESULTS = 6

    connect() {
        this.matches = []
        this.active = -1
    }

    search() {
        this._unpick()
        const query = this.inputTarget.value.trim().toLowerCase()
        if (!query) {
            this._close()
            return
        }
        this.matches = this.peopleValue
            .filter((person) => this._matches(person.name, query))
            .slice(0, this.constructor.MAX_RESULTS)
        this.active = this.matches.length ? 0 : -1
        this._render(query)
    }

    keydown(event) {
        const open = !this.resultsTarget.hidden && this.matches.length
        if (event.key === "ArrowDown" && open) {
            event.preventDefault()
            this._highlight(Math.min(this.active + 1, this.matches.length - 1))
        } else if (event.key === "ArrowUp" && open) {
            event.preventDefault()
            this._highlight(Math.max(this.active - 1, 0))
        } else if (event.key === "Enter" && open && this.active >= 0) {
            event.preventDefault()
            this._pick(this.matches[this.active])
        } else if (event.key === "Escape" && !this.resultsTarget.hidden) {
            // Close the list, not the modal.
            event.preventDefault()
            event.stopPropagation()
            this._close()
        }
    }

    // A pointer press on a result blurs the input first; wait a beat so the
    // click lands before the list goes away.
    blur() {
        setTimeout(() => this._close(), 150)
    }

    pickSuggestion(event) {
        const id = Number(event.currentTarget.dataset.userId)
        const person = this.peopleValue.find((candidate) => candidate.id === id)
        if (person) this._pick(person)
    }

    // A person picked goes to #create by id; free text goes to #invite. The
    // form's token is per-form (signed for the #invite URL), so a switched
    // action carries the page-wide token instead, which Rails also accepts.
    submit() {
        const picked = !this.userIdTarget.disabled
        this.formTarget.action = picked ? this.shareUrlValue : this.inviteUrlValue
        const token = this.formTarget.querySelector("input[name='authenticity_token']")
        const pageToken = document.querySelector("meta[name='csrf-token']")?.content
        if (picked && token && pageToken) token.value = pageToken
    }

    _matches(name, query) {
        const lower = (name || "").toLowerCase()
        return lower.startsWith(query) || lower.split(/\s+/).some((word) => word.startsWith(query))
    }

    _pick(person) {
        this.inputTarget.value = person.name
        this.userIdTarget.value = person.id
        this.userIdTarget.disabled = false
        this.inputTarget.classList.add("is-picked")
        this._close()
        this.submitTarget.focus()
    }

    _unpick() {
        this.userIdTarget.value = ""
        this.userIdTarget.disabled = true
        this.inputTarget.classList.remove("is-picked")
    }

    _render(query) {
        const list = this.resultsTarget
        list.replaceChildren()
        this.matches.forEach((person, index) => {
            const row = document.createElement("button")
            row.type = "button"
            row.className = "task-share-add-result"
            row.setAttribute("role", "option")
            row.addEventListener("mousedown", (event) => event.preventDefault())
            row.addEventListener("click", () => this._pick(person))
            row.addEventListener("mouseenter", () => this._highlight(index))
            row.append(this._avatar(person))
            const text = document.createElement("span")
            text.className = "task-share-add-result-text"
            const name = document.createElement("span")
            name.textContent = person.name
            const kind = document.createElement("small")
            kind.textContent = person.kind
            text.append(name, kind)
            row.append(text)
            list.append(row)
        })
        if (!this.matches.length) {
            const hint = document.createElement("div")
            hint.className = "task-share-add-empty"
            hint.textContent = query.includes("@") ? "Press Share to invite this address" : "No one by that name. Type an email to invite them."
            list.append(hint)
        }
        list.hidden = false
        this.inputTarget.setAttribute("aria-expanded", "true")
        this._highlight(this.active)
    }

    _avatar(person) {
        if (person.avatar) {
            const img = document.createElement("img")
            img.className = "task-share-avatar"
            img.src = person.avatar
            img.alt = ""
            return img
        }
        const span = document.createElement("span")
        span.className = "task-share-avatar task-share-avatar--initials"
        span.style.background = `hsl(${person.hue}, 60%, 45%)`
        span.textContent = person.initials
        return span
    }

    _highlight(index) {
        this.active = index
        this.resultsTarget.querySelectorAll(".task-share-add-result").forEach((row, i) => {
            row.classList.toggle("is-active", i === index)
            row.setAttribute("aria-selected", i === index ? "true" : "false")
        })
    }

    _close() {
        this.resultsTarget.hidden = true
        this.inputTarget.setAttribute("aria-expanded", "false")
    }
}
